import { useEffect, useState } from 'react';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { usePosthog } from '~/hooks/usePosthog';
import { BestInDentalV2 } from '~/constants/ff.posthog';
import { useBidHistoryQuery } from './useGetBidHistory';

export interface IsBestInDentalVisibleResult {
  visible: boolean;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Access gate (GOTU-4820): PostHog flag ON and ≥1 BID membership for the
 * selected office — or for `officeId`, the location a Manage row opened the
 * page for (GOTU-19472). Shares the page-1 query cache entry with the BID page
 * itself, so navigating to the page never double-fetches.
 *
 * Gates on `totalCount`, not the row array (GOTU-5630): an out-of-range page
 * returns `cycles: []` with a non-zero `totalCount`, and a member with no
 * memberships gets 200 + `totalCount: 0` (never null, never 403).
 *
 * `isLoading` surfaces the in-flight state so route guards can defer their
 * redirect decision until the gate has actually resolved (GOTU-5641 final
 * review): consumers must not treat `visible: false` as authoritative while
 * `isLoading` is true, or direct URL access will be redirected before the
 * flag/query settle.
 */
export const useIsBestInDentalVisible = (officeId?: string): IsBestInDentalVisibleResult => {
  const posthogService = usePosthog();
  const isFlagOn = useFeatureFlag(BestInDentalV2);
  const query = useBidHistoryQuery(1, isFlagOn, officeId);

  // GOTU-8247: a cold deeplink used to settle on `false` one tick after mount
  // and redirect before PostHog had answered. Wait for the real signal —
  // `areFlagsReady()` for a synchronous cache hit, the onFeatureFlags callback
  // otherwise. `useFeatureFlag` updates its value from that same emission, so
  // value and resolution land in one render.
  const [areFlagsResolved, setAreFlagsResolved] = useState(() => posthogService.areFlagsReady());

  useEffect(() => {
    if (areFlagsResolved) return undefined;

    let cancelled = false;
    const resolve = () => {
      if (!cancelled) setAreFlagsResolved(true);
    };

    if (posthogService.areFlagsReady()) {
      resolve();
      return undefined;
    }

    // With no PostHog instance (disabled, or no API key in this build) the
    // service drops `onFeatureFlags` on the floor and `areFlagsReady()` can
    // never become true, so there is nothing to wait for.
    if (!posthogService.getPosthogInstance()) {
      resolve();
      return undefined;
    }

    // The service's `onFeatureFlags` wrapper discards the unsubscribe posthog-js
    // returns, so there is nothing to call on cleanup — hence the `cancelled`
    // guard against a post-unmount setState.
    // posthog-js fires this callback on a failed or blocked flags request too
    // (with `errorsLoading`), so an ad-blocker resolves the wait rather than
    // hanging it; no timeout is needed, and one would re-create the cold-deeplink
    // redirect on a slow network.
    posthogService.onFeatureFlags(resolve);

    return () => {
      cancelled = true;
    };
  }, [posthogService, areFlagsResolved]);

  return {
    visible: isFlagOn && (query.data?.totalCount ?? 0) > 0,
    // Only a FALSE flag reading can produce the wrong redirect, so only that
    // reading waits. `useFeatureFlag` seeds itself from PostHog's localStorage
    // cache, which resolves before `areFlagsReady()` flips — a returning
    // eligible office must not be parked on a spinner for an answer it has.
    isLoading: (!areFlagsResolved && !isFlagOn) || (isFlagOn && query.isLoading),
    isError: isFlagOn && query.isError,
  };
};

/**
 * The same access gate for a location that is not the selected office
 * (Location settings, GOTU-15553): flag ON and ≥1 BID membership for that
 * location, asked with its own `x-office` and cached under it.
 *
 * @param officeId - the location to ask about
 * @param enabled - off for the selected office, which reads `useIsBestInDentalVisible`
 */
export const useIsOfficeBestInDentalVisible = (
  officeId: string | undefined,
  enabled: boolean,
): boolean => {
  const isFlagOn = useFeatureFlag(BestInDentalV2);
  const query = useBidHistoryQuery(1, isFlagOn && enabled && !!officeId, officeId);

  return isFlagOn && (query.data?.totalCount ?? 0) > 0;
};
